import React from 'react';
import { Card, Box, Typography, Avatar, Divider } from '@mui/material';
import { QRCodeSVG } from 'qrcode.react';
import { styled } from '@mui/material/styles';
import logo from '../assets/logo.webp';

// Estilos personalizados para la tarjeta
const StyledCard = styled(Card)(({ theme, membershiplevel }) => ({
  width: 400,
  height: 800,
  background: membershiplevel === 'GOLD' 
    ? 'linear-gradient(150deg, #FFD700 0%, #FDB931 100%)'
    : membershiplevel === 'SILVER'
    ? 'linear-gradient(150deg, #C0C0C0 0%, #A9A9A9 100%)'
    : membershiplevel === 'BRONZE'
    ? 'linear-gradient(150deg, #CD7F32 0%, #B87333 100%)'
    : 'linear-gradient(150deg, #f3f3f3 0%, #e0e0e0 100%)',
  color: membershiplevel === 'GOLD' ? theme.palette.getContrastText('#FFD700') : theme.palette.text.primary,
  position: 'relative',
  overflow: 'visible',
  borderRadius: '20px',
  boxShadow: '0 8px 16px rgba(0,0,0,0.2)',
  display: 'flex',
  flexDirection: 'column',
  padding: theme.spacing(3),
}));

const StatusBadge = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 24,
  right: 24,
  padding: '4px 12px',
  borderRadius: '12px',
  backgroundColor: 'rgba(255, 255, 255, 0.2)',
  fontSize: '0.75rem',
  fontWeight: 500,
}));

const InfoLabel = styled(Typography)({
  fontSize: '0.75rem',
  color: 'rgba(255, 255, 255, 0.7)',
  textTransform: 'uppercase',
  letterSpacing: '0.1em',
  marginBottom: '4px',
});

const InfoValue = styled(Typography)({
  fontSize: '1rem',
  color: 'white',
  fontWeight: 500,
  marginBottom: '16px',
});

const BenefitItem = styled(Typography)({
  fontSize: '0.85rem',
  color: 'rgba(255, 255, 255, 0.9)',
  marginBottom: '8px',
  display: 'flex',
  alignItems: 'center',
  '&:before': {
    content: '"•"',
    marginRight: '8px',
    color: 'rgba(255, 255, 255, 0.6)',
  }
});

const getLevelBenefits = (level) => {
  const benefits = {
    BRONZE: [
      "Acumula 1 punto por cada $1 en compras",
      "Acceso a ofertas exclusivas",
      "Newsletter mensual"
    ],
    SILVER: [
      "5% de descuento en todas las compras",
      "Acumula 1.5 puntos por cada $1",
      "Envío gratis en compras mayores a $50"
    ],
    GOLD: [
      "10% de descuento en todas las compras",
      "Acumula 2 puntos por cada $1",
      "Envío gratis en todas las compras"
    ]
  };
  return benefits[level] || benefits.BRONZE;
};

// Componente de la tarjeta de membresía
const MembershipCard = ({ 
  memberInfo = {
    name: "Usuario",
    level: "BRONZE",
    membershipId: "6489722",
    status: "Active",
    points: 0,
    avatarUrl: null
  }
}) => {
  const benefits = getLevelBenefits(memberInfo.level);

  return (
    <Box sx={{ 
      position: 'relative',
      padding: '20px',
      '&:hover': {
        '& .membership-card': {
          transform: 'translateY(-5px)',
        }
      }
    }}>
      <StyledCard className="membership-card" membershiplevel={memberInfo.level} sx={{ transition: 'transform 0.3s ease' }}>
        {/* Logo y Status */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box 
              component="img" 
              src={logo} 
              alt="Logo" 
              sx={{ 
                height: 40,
                width: 40,
                objectFit: 'contain'
              }} 
            />
            <Typography sx={{ 
              fontSize: '1.4rem',
              fontWeight: 500,
            }}>
              The Kosmo Studio
            </Typography>
          </Box>
          <StatusBadge>
            {memberInfo.status}
          </StatusBadge>
        </Box>

        {/* Información del miembro */}
        <Box sx={{ mb: 3 }}>
          <InfoLabel>MEMBER NAME</InfoLabel>
          <InfoValue>{memberInfo.name}</InfoValue>

          <InfoLabel>MEMBERSHIP LEVEL</InfoLabel>
          <InfoValue>{memberInfo.level}</InfoValue>

          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
            <Box>
              <InfoLabel>POINTS</InfoLabel>
              <InfoValue>{memberInfo.points} pts</InfoValue>
            </Box>
            <Box sx={{ textAlign: 'right' }}>
              <InfoLabel>MEMBERSHIP ID</InfoLabel>
              <InfoValue>{memberInfo.membershipId}</InfoValue>
            </Box>
          </Box>
        </Box>

        {/* Avatar */}
        <Box sx={{ 
          display: 'flex', 
          justifyContent: 'center', 
          mb: 3 
        }}>
          <Avatar 
            src={memberInfo.avatarUrl} 
            sx={{ 
              width: 100, 
              height: 100,
              border: '4px solid rgba(255,255,255,0.2)'
            }}
          />
        </Box>

        {/* Beneficios */}
        <Box sx={{ mb: 4 }}>
          <Divider sx={{ mb: 2, borderColor: 'rgba(255,255,255,0.2)' }} />
          <InfoLabel sx={{ mb: 2 }}>LEVEL BENEFITS</InfoLabel>
          {benefits.map((benefit, index) => (
            <BenefitItem key={index}>
              {benefit}
            </BenefitItem>
          ))}
        </Box>

        {/* QR Code */}
        <Box sx={{ 
          display: 'flex',
          justifyContent: 'center',
          mt: 'auto',
          mb: 2
        }}>
          <Box sx={{ 
            backgroundColor: '#fff',
            padding: 2,
            borderRadius: 2,
            width: 'fit-content',
            boxShadow: '0 4px 8px rgba(0,0,0,0.1)'
          }}>
            <QRCodeSVG 
              value={memberInfo.membershipId}
              size={150}
              level="H"
              includeMargin={false}
            />
          </Box>
        </Box>
      </StyledCard>
    </Box>
  );
};

export default MembershipCard;
